/**
 * Shared types, helpers, and components for site detail tabs
 */

import { Badge } from '@/components/ui/badge'
import { differenceInDays } from 'date-fns'
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
} from 'lucide-react'
import type { Site, SiteSettings, AllowedEmail } from '../../hooks/useSites'

// Re-export types from hooks
export type { Site, SiteSettings, LoginLog, AllowedEmail } from '../../hooks/useSites'

// ============================================================================
// TYPES
// ============================================================================

export interface DiagnosticsHealth {
  server?: {
    php_version?: string
    php_memory_limit?: string
    max_execution?: string
    upload_max?: string
    wp_version?: string
  }
  disk?: {
    free_gb?: number
    total_gb?: number
    used_pct?: number
  }
  tests?: Record<string, {
    label?: string
    status?: string
    description?: string
  }>
}

export interface CronJob {
  hook: string
  next_run: string
  next_run_diff?: string
  schedule: string
  interval?: number
  overdue?: boolean
}

export interface DatabaseInfo {
  total_size_mb?: number
  wp_tables?: Array<{
    name: string
    rows: number
    size_mb: number
  }>
  issues?: Array<{
    type: string
    message: string
  }>
}

export interface ErrorEntry {
  date?: string
  level?: string
  message?: string
  file?: string
  line?: number
}

// WordPress roles for dropdown
export type WPRole = 'administrator' | 'editor' | 'author' | 'contributor' | 'subscriber'
export const WP_ROLES: { value: WPRole; label: string }[] = [
  { value: 'administrator', label: 'Administrator' },
  { value: 'editor', label: 'Editor' },
  { value: 'author', label: 'Author' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'subscriber', label: 'Subscriber' },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse settings from site (handles both string and object)
 */
export function parseSiteSettings(settings: Site['settings']): SiteSettings {
  if (!settings) return {}
  if (typeof settings === 'string') {
    try {
      return JSON.parse(settings)
    } catch {
      return {}
    }
  }
  return settings
}

/**
 * Parse allowed domains from site
 */
export function parseAllowedDomains(domains: Site['allowed_domains']): string[] {
  if (!domains) return []
  if (typeof domains === 'string') {
    try {
      return JSON.parse(domains)
    } catch {
      return []
    }
  }
  return domains
}

/**
 * Parse allowed emails from site (handles new {email, role} format)
 */
export function parseAllowedEmails(emails: Site['allowed_emails']): AllowedEmail[] {
  if (!emails) return []
  if (typeof emails === 'string') {
    try {
      const parsed = JSON.parse(emails)
      if (!Array.isArray(parsed)) return []
      // Handle both old string[] and new {email, role}[] formats
      return parsed.map((item: string | AllowedEmail) => {
        if (typeof item === 'string') {
          return { email: item, role: 'administrator' as const }
        }
        return {
          email: item.email,
          role: item.role || 'administrator',
        }
      })
    } catch {
      return []
    }
  }
  return emails
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

export function StatusBadge({ status }: { status: Site['status'] }) {
  const variants: Record<Site['status'], { variant: 'default' | 'secondary' | 'destructive'; label: string; icon: React.ReactNode }> = {
    approved: { variant: 'default', label: 'Approved', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
    pending: { variant: 'secondary', label: 'Pending', icon: <Clock className="h-3 w-3 mr-1" /> },
    rejected: { variant: 'destructive', label: 'Rejected', icon: <XCircle className="h-3 w-3 mr-1" /> },
  }
  const { variant, label, icon } = variants[status]
  return (
    <Badge variant={variant} className="flex items-center w-fit">
      {icon}
      {label}
    </Badge>
  )
}

export function HealthBadge({ lastActivityAt }: { lastActivityAt: string | null }) {
  if (!lastActivityAt) {
    return (
      <Badge variant="destructive" className="flex items-center w-fit">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Never connected
      </Badge>
    )
  }

  const daysSinceActivity = differenceInDays(new Date(), new Date(lastActivityAt))

  if (daysSinceActivity >= 30) {
    return (
      <Badge variant="destructive" className="flex items-center w-fit">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Inactive 30+ days
      </Badge>
    )
  }

  if (daysSinceActivity >= 7) {
    return (
      <Badge variant="outline" className="flex items-center w-fit border-yellow-500 text-yellow-600 dark:text-yellow-400">
        <Clock className="h-3 w-3 mr-1" />
        Inactive 7+ days
      </Badge>
    )
  }

  return (
    <Badge variant="outline" className="flex items-center w-fit border-green-500 text-green-600 dark:text-green-400">
      <CheckCircle className="h-3 w-3 mr-1" />
      Active
    </Badge>
  )
}

export function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      <div className="text-muted-foreground">{icon}</div>
      <div>
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  )
}
